import { Container } from '@chakra-ui/react'
import {
  ExhibitionAndEventTabButtonGroups,
  Layout,
  MuseumProgramWhatson,
  Hero,
  CollectionFilterByTypeWithCheckbox,
  CollectionSearchPagination,
} from '@common'
import {
  ISSRPageData,
  IEventCard,
  IExhibitionAndEventActiveCollectionName,
} from '@interfaces'
import { EventService, ExhibitionService, ToursService } from '@services'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  generateTabCollection,
  normalizeSearchText,
  isLearningCard,
  isChildLearningCard,
  filterAdultLearningCards,
  filterChildLearningCards,
  mergeMuseumProgramExhibitions,
} from '@utils'
import { CollectionPaginationText } from '@ui'
import { usePagination } from '@lib/hooks'
import 'moment/locale/tr'

const EMPTY_EVENT_CARDS: IEventCard[] = []
const EMPTY_TAB_DATA: any[] = []

const Whatson = ({ pageData, global: { navigation } }: ISSRPageData) => {
  const { t } = useTranslation()
  const router = useRouter()
  const { asPath, locale } = router

  type ActiveWithAll = IExhibitionAndEventActiveCollectionName | 'all'
  const [activeCollection, setActiveCollection] = useState<ActiveWithAll>('all')
  /** Öğrenme: [güncel program, geçmiş program] — yalnız yetişkin / çocuk olmayan öğrenme */
  const [learningChecks, setLearningChecks] = useState<[boolean, boolean]>([
    true,
    false,
  ])
  /** Çocuk: [güncel program, geçmiş program] — öğrenme verisinden çocuk içerikleri */
  const [childChecks, setChildChecks] = useState<[boolean, boolean]>([
    true,
    false,
  ])
  const [exhibitionChecks, setExhibitionChecks] = useState<[boolean, boolean]>([
    true,
    false,
  ])
  const [eventChecks, setEventChecks] = useState<
    [boolean, boolean, boolean, boolean]
  >([true, true, true, false])

  const baseTabData = pageData?.data?.currentMuseumProgram || EMPTY_TAB_DATA
  const [pastLearningPrograms, setPastLearningPrograms] = useState<
    IEventCard[]
  >(pageData?.data?.pastLearningPrograms || EMPTY_EVENT_CARDS)
  const [pastEventPrograms, setPastEventPrograms] = useState<IEventCard[]>(
    pageData?.data?.pastEventPrograms || EMPTY_EVENT_CARDS
  )
  const [pastExhibitionPrograms, setPastExhibitionPrograms] = useState<
    IEventCard[]
  >(pageData?.data?.pastExhibitionPrograms || EMPTY_EVENT_CARDS)

  const fetchPastPrograms = async (
    kind: 'event' | 'learning' | 'exhibition'
  ) => {
    try {
      const response = await fetch(
        `/api/whatson/past?kind=${kind}&locale=${locale || 'tr'}`
      )
      const result = await response.json()
      const data = Array.isArray(result?.data) ? result.data : []
      if (kind === 'learning') {
        setPastLearningPrograms(data)
      } else if (kind === 'exhibition') {
        setPastExhibitionPrograms(data)
      } else {
        setPastEventPrograms(data)
      }
    } catch (error) {
      console.error(`Whatson ${kind} past fallback error:`, error)
    }
  }

  const scrollToTabs = () => {
    if (containerRef?.current && typeof window !== 'undefined') {
      const yCoordinate =
        (containerRef.current as HTMLDivElement).getBoundingClientRect().top +
        window.pageYOffset
      const yOffset = -40
      window.scrollTo({ top: yCoordinate + yOffset, behavior: 'smooth' })
    }
  }

  // Dil değişiminde (client-side locale switch) geçmiş program state'i
  // eski dilde kalmasın diye pageData ile senkronize et.
  useEffect(() => {
    setPastLearningPrograms(
      pageData?.data?.pastLearningPrograms || EMPTY_EVENT_CARDS
    )
    setPastEventPrograms(pageData?.data?.pastEventPrograms || EMPTY_EVENT_CARDS)
    setPastExhibitionPrograms(
      pageData?.data?.pastExhibitionPrograms || EMPTY_EVENT_CARDS
    )
  }, [
    locale,
    pageData?.data?.pastLearningPrograms,
    pageData?.data?.pastEventPrograms,
    pageData?.data?.pastExhibitionPrograms,
  ])

  useEffect(() => {
    const learningPastOn = learningChecks[1]
    const childPastOn = childChecks[1]
    if ((learningPastOn || childPastOn) && pastLearningPrograms.length === 0) {
      fetchPastPrograms('learning')
    }
  }, [learningChecks, childChecks, pastLearningPrograms.length, locale])

  useEffect(() => {
    const eventPastOn = eventChecks[3]
    if (eventPastOn && pastEventPrograms.length === 0) {
      fetchPastPrograms('event')
    }
  }, [eventChecks, pastEventPrograms.length, locale])

  useEffect(() => {
    const exhibitionPastOn = exhibitionChecks[1]
    if (exhibitionPastOn && pastExhibitionPrograms.length === 0) {
      fetchPastPrograms('exhibition')
    }
  }, [exhibitionChecks, pastExhibitionPrograms.length, locale])

  const filteredContent: IEventCard[] = useMemo(() => {
    const getTabContentByTx = (tx: string): IEventCard[] => {
      const tab = baseTabData.find((t) => t.tabLabelTx === tx)
      return tab && Array.isArray(tab.tabContent) ? tab.tabContent : []
    }

    let content: IEventCard[] = []

    switch (activeCollection) {
      case 'exhibitions': {
        const [currentOn, exhibitionPastOn] = exhibitionChecks
        const currentExhibitions = getTabContentByTx('common:tabs.exhibitions')
        const merged: IEventCard[] = []
        if (currentOn) {
          merged.push(...currentExhibitions)
        }
        if (exhibitionPastOn) {
          merged.push(...pastExhibitionPrograms)
        }
        content = merged
        break
      }
      case 'events': {
        content = getTabContentByTx('common:tabs.events')
        const [yogaOn, theaterOn, cinemaOn, pastOn] = eventChecks
        if (pastOn) {
          content = [...pastEventPrograms]
        }
        content = content.filter((eventItem) => {
          if (yogaOn && theaterOn && cinemaOn) {
            return true
          }

          const tagsText = normalizeSearchText(
            (eventItem.tagsTx || []).join(' ')
          )
          const titleText = normalizeSearchText(eventItem.title || '')
          const urlText = normalizeSearchText(eventItem.url || '')
          const searchableText = `${tagsText} ${titleText} ${urlText}`

          const isYoga =
            searchableText.includes('muzede yoga') ||
            searchableText.includes(' yoga')
          const isTheater =
            searchableText.includes('muzede sahne') ||
            searchableText.includes(' sahne') ||
            searchableText.includes(' tiyatro')
          const isCinema =
            searchableText.includes('muzede suare') ||
            searchableText.includes(' suare') ||
            searchableText.includes(' sinema')

          if (yogaOn && isYoga) return true
          if (theaterOn && isTheater) return true
          if (cinemaOn && isCinema) return true
          return false
        })
        break
      }
      case 'learning': {
        const learningTab = getTabContentByTx('common:tabs.learningPrograms')
        const [learningCurrentOn, learningPastOn] = learningChecks
        const mergedLearning: IEventCard[] = []
        if (learningCurrentOn) {
          mergedLearning.push(...learningTab)
        }
        if (learningPastOn) {
          mergedLearning.push(...pastLearningPrograms)
        }
        content = mergedLearning
          .filter(isLearningCard)
          .filter((c) => !isChildLearningCard(c))
        break
      }
      case 'children': {
        const learningTabForKids = getTabContentByTx(
          'common:tabs.childrenPrograms'
        )
        const [childCurrentOn, childPastOn] = childChecks
        const mergedKids: IEventCard[] = []
        if (childCurrentOn) {
          mergedKids.push(...learningTabForKids)
        }
        if (childPastOn) {
          mergedKids.push(...pastLearningPrograms)
        }
        content = mergedKids.filter(isLearningCard).filter(isChildLearningCard)
        break
      }
      case 'tours':
        content = getTabContentByTx('common:tabs.tours')
        break
      case 'workshop':
        content = []
        break
      case 'online':
        content = []
        break
      default:
        content = getTabContentByTx('common:tabs.allTab')
    }

    return content
  }, [
    activeCollection,
    baseTabData,
    learningChecks,
    pastLearningPrograms,
    childChecks,
    exhibitionChecks,
    pastExhibitionPrograms,
    eventChecks,
    pastEventPrograms,
  ])

  const {
    currentPage,
    totalPage,
    handlePagination,
    paginatedData,
    containerRef,
  } = usePagination(filteredContent, 24, 300)
  const hasMountedRef = useRef(false)

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true
      return
    }
    if (containerRef?.current) {
      containerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [currentPage, containerRef])

  const derivedTabData = useMemo(() => {
    return [generateTabCollection('allTab', paginatedData as IEventCard[])]
  }, [paginatedData])

  const learningProgramFilterTypes = [
    {
      id: 'learning-current',
      labelTx: 'exhibition-and-event:home.sort.currentProgram',
      type: 'current',
    },
    {
      id: 'learning-past',
      labelTx: 'exhibition-and-event:home.sort.pastProgram',
      type: 'past',
    },
  ]
  const childProgramFilterTypes = [
    {
      id: 'child-current',
      labelTx: 'exhibition-and-event:home.sort.currentProgram',
      type: 'current',
    },
    {
      id: 'child-past',
      labelTx: 'exhibition-and-event:home.sort.pastProgram',
      type: 'past',
    },
  ]
  const eventFilterTypes = [
    {
      id: 'event-yoga',
      labelTx: 'common:eventCard.well_being',
      type: 'yoga',
    },
    {
      id: 'event-theater',
      labelTx: 'common:eventCard.performing',
      type: 'theater',
    },
    {
      id: 'event-cinema',
      labelTx: 'common:eventCard.film',
      type: 'cinema',
    },
    {
      id: 'event-past',
      labelTx: 'exhibition-and-event:home.sort.pastProgram',
      type: 'past',
    },
  ]
  const exhibitionFilterTypes = [
    {
      id: 'exhibition-current',
      labelTx: 'exhibition-and-event:home.sort.currentProgram',
      type: 'current',
    },
    {
      id: 'exhibition-past',
      labelTx: 'exhibition-and-event:home.sort.pastProgram',
      type: 'past',
    },
  ]

  return (
    <Layout
      title={t('common:ssmPageTitle')}
      navigation={navigation}
      path={asPath}
    >
      <Hero
        bgImageUrl={
          'https://drupal.sakipsabancimuzesi.org/sites/default/files/2026-04/GC304189.jpg'
        }
        subTitle={t('exhibition-and-event:home.hero.subTitle')}
        title={t('exhibition-and-event:home.title')}
        //description={t('exhibition-and-event:home.hero.description')}
      />

      <Container
        maxW="container.lg"
        mt={10}
        px={{ base: 10, md: 10 }}
        ref={containerRef as any}
      >
        <ExhibitionAndEventTabButtonGroups
          disableRouting
          showAllButton
          activeOverride={activeCollection}
          onChangeActive={(next) => {
            setActiveCollection(next)
            scrollToTabs()
          }}
          containerProps={{
            mx: 0,
            w: '100%',
            justifyContent: 'flex-start',
            overflowX: 'auto',
            overflowY: 'hidden',
            flexWrap: 'nowrap',
            pb: { base: 3, lg: 1 },
            sx: {
              '& > * + *': {
                ml: '20px',
              },
              '&::-webkit-scrollbar': {
                height: '3px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'white.primary3',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'red.primary',
              },
            },
          }}
          buttonProps={{
            whiteSpace: 'nowrap',
            flexShrink: 0,
            fontFamily: 'duruSans',
            height: { base: '42px', lg: '50px' },
            fontSize: { base: 'xs', lg: 'sm' },
            px: { base: 3, lg: 4 },
          }}
        />
      </Container>
      <Container maxW="container.lg" mt={2} mb={10} px={{ base: 0, md: 4 }}>
        {activeCollection === 'learning' && (
          <CollectionFilterByTypeWithCheckbox
            inline
            collectionListingFilterTypes={learningProgramFilterTypes as any}
            checkedListingFilterTypes={learningChecks as any}
            setCheckedListingFilterTypes={
              ((val: boolean[]) => {
                const currentOn = !!val[0]
                const pastOn = !!val[1]
                if (pastOn && pastLearningPrograms.length === 0) {
                  fetchPastPrograms('learning')
                }
                if (!currentOn && !pastOn) {
                  setLearningChecks([true, false])
                  return
                }
                setLearningChecks([currentOn, pastOn])
              }) as any
            }
          />
        )}
        {activeCollection === 'children' && (
          <CollectionFilterByTypeWithCheckbox
            inline
            collectionListingFilterTypes={childProgramFilterTypes as any}
            checkedListingFilterTypes={childChecks as any}
            setCheckedListingFilterTypes={
              ((val: boolean[]) => {
                const currentOn = !!val[0]
                const pastOn = !!val[1]
                if (pastOn && pastLearningPrograms.length === 0) {
                  fetchPastPrograms('learning')
                }
                if (!currentOn && !pastOn) {
                  setChildChecks([true, false])
                  return
                }
                setChildChecks([currentOn, pastOn])
              }) as any
            }
          />
        )}
        {activeCollection === 'exhibitions' && (
          <CollectionFilterByTypeWithCheckbox
            inline
            collectionListingFilterTypes={exhibitionFilterTypes as any}
            checkedListingFilterTypes={exhibitionChecks as any}
            setCheckedListingFilterTypes={
              ((val: boolean[]) => {
                const currentOn = !!val[0]
                const pastOn = !!val[1]
                if (pastOn && pastExhibitionPrograms.length === 0) {
                  fetchPastPrograms('exhibition')
                }
                if (!currentOn && !pastOn) {
                  setExhibitionChecks([true, false])
                  return
                }
                setExhibitionChecks([currentOn, pastOn])
              }) as any
            }
          />
        )}
        {activeCollection === 'events' && (
          <CollectionFilterByTypeWithCheckbox
            inline
            collectionListingFilterTypes={eventFilterTypes as any}
            checkedListingFilterTypes={eventChecks as any}
            setCheckedListingFilterTypes={
              ((val: boolean[]) => {
                const yogaOn = !!val[0]
                const theaterOn = !!val[1]
                const cinemaOn = !!val[2]
                const pastOn = !!val[3]
                if (pastOn && pastEventPrograms.length === 0) {
                  fetchPastPrograms('event')
                }
                if (!yogaOn && !theaterOn && !cinemaOn) {
                  setEventChecks([true, true, true, pastOn])
                  return
                }
                setEventChecks([yogaOn, theaterOn, cinemaOn, pastOn])
              }) as any
            }
          />
        )}

        <MuseumProgramWhatson tabData={derivedTabData} />
        <CollectionSearchPagination
          currentPage={currentPage}
          totalPage={totalPage}
          handlePagination={handlePagination}
        />
        {totalPage > 1 && (
          <CollectionPaginationText
            activeLengthOnPage={(paginatedData as IEventCard[]).length}
            totalLength={filteredContent.length}
          />
        )}
      </Container>
    </Layout>
  )
}

export default Whatson

const fetchWithRetry = async <T,>(
  fn: () => Promise<T>,
  retries = 2,
  delayMs = 250
): Promise<T> => {
  let lastError: unknown
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await fn()
    } catch (error) {
      lastError = error
      if (attempt < retries) {
        await new Promise((resolve) => setTimeout(resolve, delayMs))
      }
    }
  }
  throw lastError
}

export async function getStaticProps({ locale }: { locale: string }) {
  const pageData: any = {
    data: {
      currentMuseumProgram: [],
      pastLearningPrograms: [],
      pastEventPrograms: [],
      pastExhibitionPrograms: [],
    },
    error: null,
  }

  try {
    const [
      currentEvents,
      futureEvents,
      currentExhibitions,
      futureExhibitions,
      currentLearnings,
      futureLearnings,
      tours,
      pastLearnings,
      pastEvents,
      pastExhibitions,
    ] = await Promise.all([
      fetchWithRetry(() =>
        EventService.getEventsBySortName(locale, 'Event', 'Current', 'page=1')
      ).catch(() => ({ data: [] })),
      fetchWithRetry(() =>
        EventService.getEventsBySortName(locale, 'Event', 'Future', 'page=1')
      ).catch(() => ({ data: [] })),
      fetchWithRetry(() =>
        ExhibitionService.getExhibitionBySortName(locale, 'current', 'page=1')
      ).catch(() => ({ data: [] })),
      fetchWithRetry(() =>
        ExhibitionService.getExhibitionBySortName(locale, 'future', 'page=1')
      ).catch(() => ({ data: [] })),
      fetchWithRetry(() =>
        EventService.getEventsBySortName(
          locale,
          'Learning',
          'Current',
          'page=1'
        )
      ).catch(() => ({ data: [] })),
      fetchWithRetry(() =>
        EventService.getEventsBySortName(locale, 'Learning', 'Future', 'page=1')
      ).catch(() => ({ data: [] })),
      fetchWithRetry(() => ToursService.getTourCategories(locale)).catch(
        () => []
      ),
      fetchWithRetry(() =>
        EventService.getEventsBySortName(locale, 'Learning', 'Past', 'page=1')
      ).catch(() => ({ data: [] })),
      fetchWithRetry(() =>
        EventService.getEventsBySortName(locale, 'Event', 'Past', 'page=1')
      ).catch(() => ({ data: [] })),
      fetchWithRetry(() =>
        ExhibitionService.getExhibitionBySortName(locale, 'past', 'page=1')
      ).catch(() => ({ data: [] })),
    ])

    const exhibitions = mergeMuseumProgramExhibitions(
      currentExhibitions?.data,
      futureExhibitions?.data
    )
    const events = [
      ...(currentEvents?.data || []),
      ...(futureEvents?.data || []),
    ]
    const learnings = [
      ...(currentLearnings?.data || []),
      ...((futureLearnings?.data || []).concat().reverse().slice(0, 30) || []),
    ]
    const learningsAdult = filterAdultLearningCards(learnings)
    const learningsChildren = filterChildLearningCards(learnings)

    pageData.data = {
      currentMuseumProgram: [
        generateTabCollection('exhibitions', exhibitions),
        generateTabCollection('events', events),
        generateTabCollection('learningPrograms', learningsAdult),
        generateTabCollection('childrenPrograms', learningsChildren),
        generateTabCollection('tours', tours || []),
        generateTabCollection('allTab', [
          ...exhibitions,
          ...learnings,
          ...events,
          ...(tours || []),
        ]),
      ],
      pastLearningPrograms: pastLearnings?.data || [],
      pastEventPrograms: pastEvents?.data || [],
      pastExhibitionPrograms: pastExhibitions?.data || [],
    }
  } catch (err: any) {
    console.error('Whatson getStaticProps error:', err)
    if (err instanceof Error) {
      pageData.error = { message: err.message }
    }
  }

  if (!(pageData?.data?.currentMuseumProgram || []).length) {
    console.error('Whatson getStaticProps fallback: empty currentMuseumProgram')
  }

  return {
    props: { pageData },
    revalidate: 300,
  }
}
